import type {
	IAMRole, AppRegistryEntry, FeatureRegistryEntry,
	PermissionGrant, PermissionAction, RolePermission,
	AppAccessLevel, RoleAppLevel,
} from '@appic/data';

export function mapRoleRow(row: Record<string, unknown>): IAMRole {
	return {
		id: String(row.id),
		label: String(row.label),
		userClass: String(row.user_class || row.userClass) as IAMRole['userClass'],
		isSystem: Boolean(row.is_system ?? row.isSystem ?? false),
		sortOrder: Number(row.sort_order ?? row.sortOrder ?? 0),
		createdAt: String(row.created_at ?? row.createdAt ?? ''),
	};
}

export function mapAppRow(row: Record<string, unknown>): AppRegistryEntry {
	return {
		id: String(row.id),
		label: String(row.label),
		icon: row.icon ? String(row.icon) : null,
		href: row.href ? String(row.href) : null,
		sortOrder: Number(row.sort_order ?? row.sortOrder ?? 0),
		isActive: Boolean(row.is_active ?? row.isActive ?? true),
		tenantId: row.tenant_id ? String(row.tenant_id) : null,
	};
}

export function mapFeatureRow(row: Record<string, unknown>): FeatureRegistryEntry {
	return {
		id: String(row.id),
		appId: String(row.app_id ?? row.appId),
		label: String(row.label),
		icon: row.icon ? String(row.icon) : null,
		href: row.href ? String(row.href) : null,
		sortOrder: Number(row.sort_order ?? row.sortOrder ?? 0),
		isActive: Boolean(row.is_active ?? row.isActive ?? true),
	};
}

export function mapPermissionGrant(row: Record<string, unknown>): PermissionGrant {
	return {
		appId: String(row.app_id ?? row.appId),
		featureId: row.feature_id != null ? String(row.feature_id) : null,
		action: String(row.action) as PermissionAction,
		granted: Boolean(row.granted),
		source: String(row.source ?? 'role') as 'user' | 'role',
		validUntil: row.valid_until ? String(row.valid_until) : null,
		maxInvocations: row.max_invocations != null ? Number(row.max_invocations) : null,
		currentInvocations: row.current_invocations != null ? Number(row.current_invocations) : null,
	};
}

export function mapRolePermission(row: Record<string, unknown>): RolePermission {
	return {
		roleId: String(row.role_id ?? row.roleId),
		appId: String(row.app_id ?? row.appId),
		featureId: row.feature_id != null ? String(row.feature_id) : null,
		action: String(row.action) as PermissionAction,
		granted: Boolean(row.granted),
		validFrom: row.valid_from ? String(row.valid_from) : null,
		validUntil: row.valid_until ? String(row.valid_until) : null,
		maxInvocations: row.max_invocations != null ? Number(row.max_invocations) : null,
		rateLimitPerHour: row.rate_limit_per_hour != null ? Number(row.rate_limit_per_hour) : null,
	};
}

export function mapAccessLevel(row: Record<string, unknown>): AppAccessLevel {
	return {
		id: String(row.id),
		appId: String(row.app_id ?? row.appId),
		levelKey: String(row.level_key ?? row.levelKey),
		label: String(row.label),
		description: row.description ? String(row.description) : null,
		sortOrder: Number(row.sort_order ?? row.sortOrder ?? 0),
		ruleCount: Number(row.rule_count ?? row.ruleCount ?? 0),
	};
}

export function mapRoleAppLevel(row: Record<string, unknown>): RoleAppLevel {
	// Nested join format: { app_id, access_level_id, app_access_levels: { level_key, label } }
	const nested = row.app_access_levels as Record<string, unknown> | undefined;
	return {
		roleId: String(row.role_id ?? row.roleId ?? ''),
		appId: String(row.app_id ?? row.appId),
		levelKey: String(nested?.level_key ?? row.level_key ?? row.levelKey ?? 'none'),
		label: String(nested?.label ?? row.label ?? ''),
	};
}
